import { openEntityDialog } from '@/constants/states';
import { readStates, saveStates, states } from '@/constants/lifecycle';
import findResult, { findWithCodes, hasKey } from '@/utils/resultsHelper';
import { pruneFieldErrors } from '@/utils/errors';
import fields from '@/constants/fields';
import {
  datasetDatasourceFeaturesCategories,
  datasetDatasourceSettingsCategories,
  datasourceFeatureStatuses,
  datasourceTypes,
  internalDatasourceType,
} from '../constants';

const determineGlobalStatus = (featureStatuses) => {
  const statuses = Object.values(datasourceFeatureStatuses);
  let globalStatus = datasourceFeatureStatuses.UNSUPPORTED;
  featureStatuses.forEach((featureStatus) => {
    if (featureStatus !== globalStatus) {
      if (statuses.indexOf(featureStatus) > statuses.indexOf(globalStatus)) {
        globalStatus = featureStatus;
      }
    }
  });
  return globalStatus;
};

const buildFeatures = (baseFeatures, featureOverrides = {}) => {
  const features = {
    ...baseFeatures,
    ...featureOverrides,
  };
  return {
    status: determineGlobalStatus(Object.values(features)),
    features,
  };
};

const cohortCategoryFields = (featureOverrides = {}) => buildFeatures({
  filter: datasourceFeatureStatuses.UNSUPPORTED,
  counts: datasourceFeatureStatuses.UNSUPPORTED,
  box_plot: datasourceFeatureStatuses.UNSUPPORTED,
  stacked_bar: datasourceFeatureStatuses.UNSUPPORTED,
  bar: datasourceFeatureStatuses.UNSUPPORTED,
  histogram: datasourceFeatureStatuses.UNSUPPORTED,
}, featureOverrides);

const ingestCategoryFields = (featureOverrides = {}) => buildFeatures({
  csv: datasourceFeatureStatuses.UNSUPPORTED,
}, featureOverrides);

const managementCategoryFields = (featureOverrides = {}) => buildFeatures({
  validate: datasourceFeatureStatuses.UNSUPPORTED,
  convert: datasourceFeatureStatuses.UNSUPPORTED,
}, featureOverrides);

const transferCategoryFields = (featureOverrides = {}) => buildFeatures({
  clear: datasourceFeatureStatuses.UNSUPPORTED,
  pseudonymisation: datasourceFeatureStatuses.UNSUPPORTED,
  token: datasourceFeatureStatuses.UNSUPPORTED,
  manual: datasourceFeatureStatuses.UNSUPPORTED,
}, featureOverrides);

const datasourceFeatures = (ingestOverrides, managementOverrides, cohortOverrides, transferOverrides) => ({
  ingest: ingestCategoryFields(ingestOverrides),
  management: managementCategoryFields(managementOverrides),
  cohort: cohortCategoryFields(cohortOverrides),
  transfer: transferCategoryFields(transferOverrides),
});

export const datasourceFields = (
  {
    isManagedOverride = false,
    typeOverride = null,
    ingestFeatureOverrides = {},
    managementFeatureOverrides = {},
    cohortFeatureOverrides = {},
    transferFeatureOverrides = {},
  } = {},
) => (
  {
    managed_data: isManagedOverride,
    type: typeOverride,
    features: datasourceFeatures(
      ingestFeatureOverrides,
      managementFeatureOverrides,
      cohortFeatureOverrides,
      transferFeatureOverrides,
    ),
  }
);

export const baseDatasourceSetting = ({
  host, name, type, password, port, username,
}) => ({
  host,
  name,
  type,
  password,
  port,
  username,
});

const testableDatasource = () => ({
  ...baseDatasourceSetting({
    host: null, name: null, type: datasourceTypes.INTERNAL, password: null, port: null, title: null, username: null,
  }),
  connection: null,
  managed_data: true,
  source_exists: false,
  testStatus: states.NONE,
  testErrors: [],
});

const baseDatasourceSettings = ({ code }, readStatus = states.NONE) => ({
  code,
  readErrors: [],
  readStatus,
  ...baseDatasourceSetting({
    host: null, name: null, type: datasourceTypes.INTERNAL, password: null, port: null, title: null, username: null,
  }),
});

export const newDatasourceSettings = ({ code }, readStatus = states.NONE) => ({
  ...baseDatasourceSettings({ code }, readStatus),
  copy: {
    code,
    isNew: true,
    ...testableDatasource(),
    ...saveStates,
  },
});

export const openDatasourceSettings = ({ code, result, duplicate }) => {
  const datasource = duplicate ? baseDatasourceSettings({ code }, states.SUCCESS) : {
    ...result,
    code,
    type: result.type || datasourceTypes.INTERNAL,
    readErrors: [],
    readStatus: states.SUCCESS,
  };

  datasource.copy = {
    ...result, code, type: result.type || datasourceTypes.INTERNAL, isNew: duplicate,
  };
  return datasource;
};

export const newDatasourceFeatures = ({ code }) => ({
  code,
  readErrors: [],
  readStatus: states.PENDING,
  ...datasourceFields(),
});

export const defaultDatasourceSetting = () => ({
  ...openEntityDialog,
  ...testableDatasource(),
  ...saveStates,
});

export const findDatasourceFeature = ({ features }, { category, code }) => findResult(features, category, code);

export const findDatasourceSetting = ({ settings }, { category, code }) => findResult(settings, category, code);

export const findDatasourceTypes = ({ datasources }, { category }) => datasources[category];

export const findCurrentDatasourceSetting = ({ settings, activeDatasourceSetting }, { category, code }) => {
  const item = findDatasourceSetting({ settings }, { category, code });
  return activeDatasourceSetting.open ? activeDatasourceSetting : item?.copy;
};

export const findAllDatasourceSettings = ({ settings }, { items }) => {
  const codes = items.map(({ code }) => code);
  return findWithCodes(settings, datasetDatasourceSettingsCategories, codes);
};

export const findAllDatasourceFeatures = ({ features }, { items }) => {
  const codes = items.map(({ code }) => code);
  return findWithCodes(features, datasetDatasourceFeaturesCategories, codes);
};

const initialState = {
  activeDatasourceSetting: defaultDatasourceSetting(),
  datasources: {
    all: {
      ...readStates,
      results: [internalDatasourceType],
    },
  },
  features: {
    open: {
      results: [],
    },
  },
  settings: {
    open: {
      results: [],
    },
  },
};

export const pruneDatasourceErrors = (errors, { properties }) => {
  let filteredErrors = errors;
  const typeChange = hasKey(properties, 'type');

  if (typeChange || hasKey(properties, 'name')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.NAME);
  }
  if (typeChange || hasKey(properties, 'host')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.HOST);
  }
  if (typeChange || hasKey(properties, 'username')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.USERNAME);
  }
  if (typeChange || hasKey(properties, 'password')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.PASSWORD);
  }

  return filteredErrors;
};

export default initialState;
